import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeSanitize from 'rehype-sanitize';
import type { Root } from 'hast';
import { mermaidSvgSchema } from '../security/sanitizeSchema';

export type MermaidTheme = 'light' | 'dark';

let initializedTheme: MermaidTheme | null = null;
let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;

const svgParser = unified()
  .use(rehypeParse, { fragment: true, space: 'svg' })
  .use(rehypeSanitize, mermaidSvgSchema);

const getMermaid = async (theme: MermaidTheme) => {
  mermaidPromise ??= import('mermaid').then((module) => module.default);
  const mermaid = await mermaidPromise;

  if (initializedTheme !== theme) {
    mermaid.initialize({
      startOnLoad: false,
      // `strict` runs Mermaid's own sanitizer and disables HTML labels.
      securityLevel: 'strict',
      htmlLabels: false,
      theme: 'base',
      themeVariables: theme === 'dark'
        ? {
            background: '#1b2928',
            primaryColor: '#294039',
            primaryTextColor: '#e8eee9',
            primaryBorderColor: '#85d4b8',
            secondaryColor: '#21332e',
            tertiaryColor: '#1b2928',
            lineColor: '#a5b8af',
            textColor: '#e8eee9',
          }
        : {
            background: '#fffefa',
            primaryColor: '#edf4ee',
            primaryTextColor: '#1d2928',
            primaryBorderColor: '#8ab3a5',
            secondaryColor: '#f1f3ed',
            tertiaryColor: '#fffefa',
            lineColor: '#60706b',
            textColor: '#1d2928',
          },
      fontFamily: '"Inter", system-ui, sans-serif',
      logLevel: 'fatal',
      flowchart: { htmlLabels: false, useMaxWidth: true },
      sequence: { useMaxWidth: true },
      gantt: { useMaxWidth: true },
      er: { useMaxWidth: true },
      class: { useMaxWidth: true },
      journey: { useMaxWidth: true },
    });
    initializedTheme = theme;
  }

  return mermaid;
};

let counter = 0;
const nextId = () => `mermaid-${Date.now().toString(36)}-${(counter += 1)}`;

export interface MermaidResult {
  tree: Root;
  id: string;
}

/**
 * Validates and renders a Mermaid definition into a sanitized HAST tree.
 *
 * Mermaid is imported lazily so a document without diagrams never pays for it,
 * and the resulting SVG is re-parsed and filtered through {@link mermaidSvgSchema}
 * before it becomes React elements.
 *
 * @throws when the diagram definition is invalid, so callers can show the error
 * against that one block instead of failing the document.
 */
export const renderMermaid = async (
  code: string,
  theme: MermaidTheme = 'light',
): Promise<MermaidResult> => {
  const definition = code.trim();
  if (definition === '') {
    throw new Error('The diagram is empty.');
  }

  const mermaid = await getMermaid(theme);
  const id = nextId();

  // `parse` throws a descriptive syntax error before anything is added to the DOM.
  await mermaid.parse(definition);
  const { svg } = await mermaid.render(id, definition);

  const tree = svgParser.runSync(svgParser.parse(svg)) as Root;
  return { tree, id };
};

/** Test seam: forces re-initialization on the next render. */
export const resetMermaid = (): void => {
  mermaidPromise = null;
  initializedTheme = null;
};
